import { FC } from 'react';

import {
  FontWeightEnum,
  Heading,
  HeadingEnum,
  Icon,
  Link,
  Message,
  MessageTypes,
} from '@argenta/react-ui';
import { faAward, faCoins, faGift, faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';

import { useToggles } from '../../hooks/useToggles';
import { ImageWithFallback } from '../ImageWithFallback';
import { ServerHtml } from '../ServerHtml';
import { VacancyApplyButton } from '../VacanceApplyButton/VacancyApplyButton';

import { VacancyDetailsProps } from './VacancyDetails.types';

import './VacancyDetails.styles.scss';

export const VacancyDetails: FC<VacancyDetailsProps> = ({ vacancyDetails }) => {
  const {
    ops: { applicationGroupsImageBaseUrl },
  } = useToggles();
  return (
    <main className="argnt-c-vacancy-details">
      <div className="argnt-c-vacancy-details__content">
        <address className="argnt-c-vacancy-details__address">
          <div className="argnt-c-vacancy-details__address--city">{vacancyDetails.addressData}</div>
        </address>
        <section className="argnt-c-vacancy-details__intro-text">
          <ServerHtml htmlString={vacancyDetails.description} />
        </section>
        <section>
          <Heading as="h2" type={HeadingEnum.H3} fontWeight={FontWeightEnum.REGULAR}>
            <Icon icon="Group" size={48} />
            <ServerHtml htmlString={vacancyDetails.teamBlock.title} />
          </Heading>
          <ServerHtml htmlString={vacancyDetails.teamBlock.description} />
        </section>
      </div>

      <ImageWithFallback
        imageSource={`${applicationGroupsImageBaseUrl}/${vacancyDetails.photoCode}.jpg`}
        fallbackImageSource={`${process.env.PUBLIC_URL}/assets/images/argenta-and-you-match.jpg`}
        alt=""
        className="argnt-c-vacancy-details__image"
      />
      <Message
        type={MessageTypes.Success}
        hideIcon
        className="argnt-c-vacancy-details__match-message"
      >
        <section>
          <Heading as="h2" type={HeadingEnum.H3} fontWeight={FontWeightEnum.REGULAR}>
            <Icon icon="RechttoeRechtaan" size={48} />
            <ServerHtml htmlString={vacancyDetails.matchBlock.title} />
          </Heading>
          <ServerHtml htmlString={vacancyDetails.matchBlock.description} />

          <Link
            className="argnt-c-vacancy-details__match-link"
            href={t({ id: 'vacancies.details.argentaMatch.link.href' })}
            target="_blank"
          >
            <Trans id="vacancies.details.argentaMatch.link.text" values={{}} components={{}} />
          </Link>
        </section>
      </Message>

      <div className="argnt-c-vacancy-details__content">
        <section>
          <Heading as="h2" type={HeadingEnum.H3} fontWeight={FontWeightEnum.REGULAR}>
            <div className="argnt-c-vacancy-details__icon-wrapper">
              <FontAwesomeIcon icon={faCoins} />
            </div>
            <ServerHtml htmlString={vacancyDetails.paycheckBlock.title} />
          </Heading>
          <ServerHtml htmlString={vacancyDetails.paycheckBlock.description} />
        </section>

        {vacancyDetails.extraBlock?.title && vacancyDetails.extraBlock?.description && (
          <section>
            <Heading as="h2" type={HeadingEnum.H3} fontWeight={FontWeightEnum.REGULAR}>
              <div className="argnt-c-vacancy-details__icon-wrapper">
                <FontAwesomeIcon icon={faGift} />
              </div>
              <ServerHtml htmlString={vacancyDetails.extraBlock.title} />
            </Heading>
            <ServerHtml htmlString={vacancyDetails.extraBlock.description} />
          </section>
        )}

        <section>
          <Heading as="h2" type={HeadingEnum.H3} fontWeight={FontWeightEnum.REGULAR}>
            <div className="argnt-c-vacancy-details__icon-wrapper">
              <FontAwesomeIcon icon={faAward} />
            </div>
            <ServerHtml htmlString={vacancyDetails.yourAssetsBlock.title} />
          </Heading>
          <ServerHtml htmlString={vacancyDetails.yourAssetsBlock.description} />
        </section>

        <section>
          <Heading as="h2" type={HeadingEnum.H3} fontWeight={FontWeightEnum.REGULAR}>
            <div className="argnt-c-vacancy-details__icon-wrapper">
              <FontAwesomeIcon icon={faInfoCircle} />
            </div>
            <ServerHtml htmlString={vacancyDetails.questionsBlock.title} />
          </Heading>
          <ServerHtml htmlString={vacancyDetails.questionsBlock.description} />
        </section>
      </div>

      <Message
        type={MessageTypes.Success}
        hideIcon
        className="argnt-c-vacancy-details__ready-message"
      >
        <section>
          <Heading as="h2" type={HeadingEnum.H3} fontWeight={FontWeightEnum.REGULAR}>
            <Trans id="vacancies.details.ready.title" values={{}} components={{}} />
          </Heading>
          <ol>
            <li>
              <Trans id="vacancies.details.ready.list1" values={{}} components={{}} />
            </li>
            <li>
              <Trans id="vacancies.details.ready.list2" values={{}} components={{}} />
            </li>
            <li>
              <Trans id="vacancies.details.ready.list3" values={{}} components={{}} />
            </li>
            <li>
              <Trans id="vacancies.details.ready.list4" values={{}} components={{}} />
            </li>
          </ol>
        </section>
      </Message>
      <VacancyApplyButton vacancyDetails={vacancyDetails} />
    </main>
  );
};
